import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { ChevronLeft, DollarSign, TrendingDown, Zap, Target, Search, Calendar, Eye, EyeOff } from 'lucide-react';
import { T, CHART_COLORS, TRADER_TYPES, TRADER_COLORS, STATUS_COLORS, fmtMonth, fmtNum, fmtMoney, safeNum, calcHealthScore, healthColor, healthLabel, calcRiskScore, calcClientGroup } from '../theme';
import { getGroupConfig } from '../riskConfig';
import { Badge, RiskBadge, StatusBadge, HealthBadge, StatCard, Card, CustomTooltip, DataTable, CoBadge, GroupBadge, HelpTip } from '../components/UI';
import { isMonitored, toggleMonitor } from './Monitoring';

const MNAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const RISK_C = { High: T.red, Moderate: T.orange, Low: T.green, Normal: T.green };
const LEVEL_COLORS = { Safe: T.green, Low: T.cyan, Elevated: T.amber, High: T.orange, Critical: T.red };

const fmtDate = (v) => {
  if (!v || v === "NULL" || v === "null" || v === "0" || v === 0) return "—";
  if (v instanceof Date && !isNaN(v)) return v.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const s = String(v).trim();
  if (!s) return "—";
  if (/^\d{1,3}:\d{2}(:\d{2})?(\.\d+)?$/.test(s)) return s;
  const n = Number(s);
  if (!isNaN(n) && n > 25000 && n < 60000) {
    const d = new Date(Math.round((n - 25569) * 86400000));
    if (!isNaN(d) && d.getFullYear() > 1990) return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  }
  let d = new Date(s);
  if (!isNaN(d) && d.getFullYear() > 1990 && d.getFullYear() < 2040) return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const dmy = s.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})/);
  if (dmy) { d = new Date(parseInt(dmy[3]), parseInt(dmy[2]) - 1, parseInt(dmy[1])); if (!isNaN(d) && d.getFullYear() > 1990) return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); }
  return s.split("T")[0];
};

const fmtDuration = (sec) => {
  const s = safeNum(sec);
  if (s <= 0) return "—";
  if (s < 60) return fmtNum(s, 0) + "s";
  if (s < 3600) return fmtNum(s / 60, 1) + "m";
  return fmtNum(s / 3600, 1) + "h";
};

const aggSum = (logins, field) => logins.reduce((s, l) => s + safeNum(l[field]), 0);
const aggMax = (logins, field) => { let m = -Infinity; logins.forEach(l => { const v = safeNum(l[field]); if (v > m) m = v; }); return m === -Infinity ? 0 : m; };
const aggMin = (logins, field) => { let m = Infinity; logins.forEach(l => { const v = safeNum(l[field]); if (v < m) m = v; }); return m === Infinity ? 0 : m; };
const aggWeightedAvg = (logins, field, weightField) => {
  let totalW = 0, totalV = 0;
  logins.forEach(l => { const w = safeNum(l[weightField]); if (w > 0) { totalW += w; totalV += safeNum(l[field]) * w; } });
  return totalW > 0 ? totalV / totalW : 0;
};
const aggUnion = (logins, field) => [...new Set(logins.map(l => l[field]).filter(v => v && v !== "NULL" && v !== "—" && v !== "None"))];
const aggWorstRisk = (logins, field) => {
  const order = ["High", "Moderate", "Low", "Normal"];
  let worst = "Normal";
  logins.forEach(l => { const v = l[field]; if (order.indexOf(v) >= 0 && order.indexOf(v) < order.indexOf(worst)) worst = v; });
  const dist = {};
  logins.forEach(l => { const v = l[field] || "Normal"; dist[v] = (dist[v] || 0) + 1; });
  return { value: worst, dist: Object.entries(dist).map(([k, v]) => v + "× " + k).join(", ") };
};

// ─── LOGIN DROPDOWN (separate component — hooks OK) ───
function LoginDropdown({ logins, selected, onSelect, loginColors }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtered = logins.filter(l => String(l.Login).includes(search) || (l.Account_Type || "").toLowerCase().includes(search.toLowerCase()) || (l.Name || "").toLowerCase().includes(search.toLowerCase()));
  const sel = selected === "ALL" ? null : logins.find(l => String(l.Login) === selected);
  const label = selected === "ALL" ? "ALL (" + logins.length + " accounts)" : "#" + (sel ? sel.Login : "") + " — " + (sel ? sel.ClientStatus || "" : "");
  const selColor = selected === "ALL" ? T.cyan : (loginColors[selected] || T.cyan);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => { setOpen(!open); setSearch(""); }}
        style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 14px", borderRadius: 8,
          border: "1px solid " + selColor + "60", background: selColor + "10",
          color: selColor, fontSize: 12, fontWeight: 600, cursor: "pointer", minWidth: 200 }}>
        {sel && <span style={{ width: 7, height: 7, borderRadius: "50%", background: STATUS_COLORS[sel.ClientStatus] || T.textMuted }} />}
        <span className="mono">{label}</span>
        <span style={{ marginLeft: "auto", fontSize: 10, opacity: 0.6 }}>▼</span>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, width: 380, maxHeight: 380, background: T.bgCard, border: "1px solid " + T.border, borderRadius: 10, zIndex: 100, boxShadow: "0 12px 40px rgba(0,0,0,0.6)", overflow: "hidden" }}>
          <div style={{ padding: "8px 10px", borderBottom: "1px solid " + T.border }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search login, name or type..." autoFocus
              style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid " + T.border, background: T.bg, color: T.text, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
          </div>
          <div onClick={() => { onSelect("ALL"); setOpen(false); }}
            style={{ padding: "10px 14px", cursor: "pointer", fontSize: 12, fontWeight: 700, background: selected === "ALL" ? T.cyanGlow : "transparent", color: T.cyan, borderBottom: "1px solid " + T.border + "30" }}
            onMouseEnter={e => selected !== "ALL" && (e.currentTarget.style.background = T.bgHover)} onMouseLeave={e => selected !== "ALL" && (e.currentTarget.style.background = "transparent")}>
            ALL — {logins.length} accounts (aggregated)
          </div>
          <div style={{ maxHeight: 285, overflowY: "auto" }}>
            {filtered.map(l => {
              const lid = String(l.Login);
              const col = loginColors[lid] || T.cyan;
              const gcfg = getGroupConfig();
              const gDef = gcfg.groups[l._clientGroup];
              const gCol = gDef ? gDef.color : T.textMuted;
              return (
                <div key={lid} onClick={() => { onSelect(lid); setOpen(false); }}
                  style={{ padding: "9px 14px", cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", gap: 8,
                    background: selected === lid ? col + "12" : "transparent", borderBottom: "1px solid " + T.border + "15",
                    borderLeft: selected === lid ? "3px solid " + col : "3px solid transparent" }}
                  onMouseEnter={e => selected !== lid && (e.currentTarget.style.background = T.bgHover)} onMouseLeave={e => selected !== lid && (e.currentTarget.style.background = selected === lid ? col + "12" : "transparent")}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: STATUS_COLORS[l.ClientStatus] || T.textMuted, flexShrink: 0 }} />
                  <span className="mono" style={{ color: col, fontWeight: 700, minWidth: 60 }}>#{lid}</span>
                  <span style={{ color: T.textDim, flex: 1, fontSize: 10, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.Account_Type || "—"}</span>
                  <span style={{ padding: "2px 6px", borderRadius: 4, fontSize: 9, fontWeight: 700, color: gCol, background: gCol + "18" }}>{l._clientGroup}</span>
                </div>
              );
            })}
            {filtered.length === 0 && <div style={{ padding: 20, textAlign: "center", color: T.textMuted, fontSize: 12 }}>No logins match</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── RISK TIMELINE ───
function RiskTimeline({ logins, selectedLogin, historyMap, loginColors }) {
  const isAll = selectedLogin === "ALL";
  const show = isAll ? logins : logins.filter(l => String(l.Login) === selectedLogin);
  if (!show.length) return null;
  const allMonths = [...new Set(logins.flatMap(l => (historyMap[l.Login] || []).map(h => h.MonthKey)))].sort();
  if (!allMonths.length) return null;
  return (
    <div style={{ marginTop: 14, paddingTop: 10, borderTop: "1px solid " + T.border + "30" }}>
      <div style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.8, marginBottom: 8 }}>
        Risk Timeline {isAll && <span style={{ fontWeight: 400, textTransform: "none", marginLeft: 6, color: T.textDim }}>(all logins overlaid)</span>}
      </div>
      {show.map(login => {
        const lid = String(login.Login);
        const history = historyMap[lid] || [];
        // Build month -> row map
        const monthRows = {};
        history.forEach(h => { monthRows[h.MonthKey] = h; });
        // Persistence: carry forward risk level during inactive months
        let lastActiveLevel = "Safe";
        const timeline = allMonths.map(m => {
          const row = monthRows[m];
          // Active = has row AND has actual trading activity (volume > 0 OR trades > 0)
          const isActive = row && (safeNum(row.Volume) > 0 || safeNum(row.TotalTrades) > 0 || safeNum(row.TotalDeals) > 0);
          if (isActive) {
            const score = calcHealthScore(row);
            const lbl = score >= 51 ? "Critical" : score >= 39 ? "High" : score >= 27 ? "Elevated" : score >= 16 ? "Low" : "Safe";
            lastActiveLevel = lbl;
            return { month: m, level: lbl, carried: false };
          }
          // Inactive month: carry forward the last known risk level (NOT reset to Safe)
          return { month: m, level: lastActiveLevel, carried: true };
        });
        return (
          <div key={lid} style={{ marginBottom: isAll ? 8 : 0 }}>
            {isAll && <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: loginColors[lid] || T.cyan, flexShrink: 0 }} />
              <span className="mono" style={{ fontSize: 10, color: loginColors[lid] || T.cyan, fontWeight: 600 }}>#{lid}</span>
            </div>}
            <div style={{ display: "flex", gap: 2 }}>
              {timeline.map((h, i) => {
                const col = LEVEL_COLORS[h.level] || T.green;
                return (
                  <div key={i} title={fmtMonth(h.month) + ": " + h.level + (h.carried ? " (carried — inactive)" : "")}
                    style={{ flex: 1, height: isAll ? 14 : 18, borderRadius: 3, background: col, opacity: h.carried ? 0.45 : 0.85, cursor: "default", position: "relative" }}>
                    {h.carried && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <div style={{ width: "60%", height: 2, background: "rgba(0,0,0,0.4)", borderRadius: 1 }} />
                    </div>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 9, color: T.textDim }}>
        <span>{fmtMonth(allMonths[0])}</span>
        <div style={{ display: "flex", gap: 8 }}>
          {Object.entries(LEVEL_COLORS).map(([k, c]) => (
            <span key={k} style={{ display: "flex", alignItems: "center", gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: c, display: "inline-block" }} />{k}</span>
          ))}
          <span style={{ display: "flex", alignItems: "center", gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: T.orange, opacity: 0.45, display: "inline-block" }} />Carried</span>
        </div>
        <span>{fmtMonth(allMonths[allMonths.length - 1])}</span>
      </div>
    </div>
  );
}

// ─── GROUP TIMELINE ───
function GroupTimelineCmp({ logins, selectedLogin, loginColors, groupHistoryMap }) {
  const isAll = selectedLogin === "ALL";
  const show = isAll ? logins : logins.filter(l => String(l.Login) === selectedLogin);
  if (!show.length) return null;
  const gcfg = getGroupConfig();
  // Always use ALL logins' months for the full timeline range
  const allMonths = [...new Set(logins.flatMap(l => (groupHistoryMap[l.Login] || []).map(h => h.month)))].sort();
  if (!allMonths.length) return null;
  return (
    <div style={{ marginTop: 14, paddingTop: 10, borderTop: "1px solid " + T.border + "30" }}>
      <div style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.8, marginBottom: 8 }}>
        Group Timeline {isAll && <span style={{ fontWeight: 400, textTransform: "none", marginLeft: 6, color: T.textDim }}>(all logins overlaid)</span>}
      </div>
      {show.map(login => {
        const lid = String(login.Login);
        const gh = groupHistoryMap[lid] || [];
        const monthGroup = {}; gh.forEach(h => { monthGroup[h.month] = h.group; });
        return (
          <div key={lid} style={{ marginBottom: isAll ? 8 : 0 }}>
            {isAll && <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: loginColors[lid] || T.cyan, flexShrink: 0 }} />
              <span className="mono" style={{ fontSize: 10, color: loginColors[lid] || T.cyan, fontWeight: 600 }}>#{lid}</span>
            </div>}
            <div style={{ display: "flex", gap: 2 }}>
              {allMonths.map((m, i) => {
                const g = monthGroup[m];
                const gDef = g ? gcfg.groups[g] : null;
                const col = gDef ? gDef.color : T.textMuted;
                return <div key={i} title={fmtMonth(m) + ": " + (g || "—")} style={{ flex: 1, height: isAll ? 14 : 18, borderRadius: 3, background: g ? col : T.bgHover, opacity: 0.85, cursor: "default" }} />;
              })}
            </div>
          </div>
        );
      })}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 9, color: T.textDim }}>
        <span>{fmtMonth(allMonths[0])}</span>
        <div style={{ display: "flex", gap: 6 }}>
          {(gcfg.groupOrder || Object.keys(gcfg.groups)).map(gk => {
            const gDef = gcfg.groups[gk]; if (!gDef) return null;
            return <span key={gk} style={{ display: "flex", alignItems: "center", gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: gDef.color, display: "inline-block" }} />{gk}</span>;
          })}
        </div>
        <span>{fmtMonth(allMonths[allMonths.length - 1])}</span>
      </div>
    </div>
  );
}

function DetailSection({ title, titleColor, subtitle, items, children }) {
  return (
    <Card accent={titleColor || T.blue}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: subtitle ? 4 : 14, color: titleColor || T.text }}>{title}</div>
      {subtitle && <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 12 }}>{subtitle}</div>}
      {items.map(([k, v, c, helpId], i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid " + T.border + "15", fontSize: 12 }}>
          <span style={{ color: T.textDim, display: "flex", alignItems: "center", gap: 4 }}>{k}{helpId && <HelpTip id={helpId} size={13} />}</span>
          {React.isValidElement(v) ? v : <span className="mono" style={{ fontWeight: 500, color: c || T.text, maxWidth: 260, textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v != null ? v : "—"}</span>}
        </div>
      ))}
      {children}
    </Card>
  );
}

// ════════════════════════════════════════════════════════
// ═══ CLIENT DETAIL VIEW (separate component for hooks) ═
// ════════════════════════════════════════════════════════
function ClientDetailView({ C, selectedClient, setSelectedClient, clientHistory, setClientHistory, showPhone, onBack, filterYear, setFilterYear, filterMonths, setFilterMonths }) {
  const [selectedLogin, setSelectedLogin] = useState("ALL");
  const [monitored, setMonitored] = useState(false);

  const baseClient = (C.healthScores || []).find(c => c.Login === selectedClient.Login && c._company === selectedClient._company) || selectedClient;
  const cuAccount = baseClient.CU_Account;
  const company = baseClient._company;
  const cuLoginsAll = (C.healthScores || []).filter(c => c.CU_Account === cuAccount && c._company === company);

  // Data version key: changes when filters change or data recomputes
  const cuLoginKey = cuLoginsAll.map(l => l.Login).sort().join(",");
  const dataVersion = C.latestMonth + "|" + (C.months || []).join(",");

  // No memo — small array, must always reflect latest C data
  const statusOrder = { Active: 0, Dormant: 1 };
  const cuLogins = [...cuLoginsAll].sort((a, b) => {
    const sa = statusOrder[a.ClientStatus] != null ? statusOrder[a.ClientStatus] : 2;
    const sb = statusOrder[b.ClientStatus] != null ? statusOrder[b.ClientStatus] : 2;
    if (sa !== sb) return sa - sb;
    return safeNum(b.Volume) - safeNum(a.Volume);
  });

  const loginColors = useMemo(() => {
    const colors = {};
    cuLogins.forEach((l, i) => { colors[String(l.Login)] = CHART_COLORS[i % CHART_COLORS.length]; });
    return colors;
  }, [cuLoginKey]);

  useEffect(() => { setSelectedLogin("ALL"); }, [selectedClient]);
  useEffect(() => { setMonitored(isMonitored(baseClient.Login, baseClient._company)); }, [baseClient.Login, baseClient._company]);

  const availYears = [...new Set((C.allMonths || []).map(m => String(m).slice(0, 4)))].sort();
  const availMonthNums = (C.allMonths || []).filter(m => filterYear === "all" || String(m).startsWith(filterYear)).map(m => parseInt(String(m).slice(4)));
  const toggleMonth = (num) => {
    if (filterMonths.includes(num)) setFilterMonths(filterMonths.filter(m => m !== num));
    else setFilterMonths([...filterMonths, num].sort((a, b) => a - b));
  };

  const isAll = selectedLogin === "ALL";
  const selLogin = cuLogins.find(l => String(l.Login) === selectedLogin);
  const cl = isAll ? baseClient : (selLogin || baseClient);
  const cuAllFlags = useMemo(() => [...new Set(cuLogins.flatMap(l => TRADER_TYPES.filter(t => l[t] === t)))], [cuLoginKey, dataVersion]);
  const activeFlags = isAll ? cuAllFlags : TRADER_TYPES.filter(t => cl[t] === t);
  const primary = cuLogins[0] || baseClient;

  const financials = useMemo(() => {
    const src = isAll ? cuLogins : [cl];
    // Period values from filtered client objects
    const deposit = src.reduce((s, l) => s + safeNum(l.Deposit), 0);
    const withdraw = Math.abs(src.reduce((s, l) => s + safeNum(l.Withdraw), 0));
    const volume = src.reduce((s, l) => s + safeNum(l.Volume), 0);
    const profit = src.reduce((s, l) => s + safeNum(l.Profit), 0);
    const trades = src.reduce((s, l) => s + safeNum(l.TotalTrades || l.TotalDeals), 0);

    // Lifetime totals from latestAllClients (absolute latest month, has T.* fields)
    const latAll = C.latestAllClients || [];
    const cuLatest = isAll
      ? latAll.filter(l => l.CU_Account === cuAccount && l._company === company)
      : latAll.filter(l => String(l.Login) === selectedLogin && l._company === company);
    const totalDeposit = cuLatest.reduce((s, l) => s + safeNum(l["T.Deposit"]), 0);
    const totalWithdraw = Math.abs(cuLatest.reduce((s, l) => s + safeNum(l["T.Withdraw"]), 0));
    const totalVolume = cuLatest.reduce((s, l) => s + safeNum(l["T.Volume"]), 0);
    const totalProfit = cuLatest.reduce((s, l) => s + safeNum(l["T.Profit"]), 0);
    // No T.TotalTrades in data — sum from historyMap for trades total
    const totalTrades = cuLatest.reduce((s, l) => {
      const hist = C.historyMap[l.Login] || [];
      return s + hist.reduce((ss, h) => ss + safeNum(h.TotalTrades || h.TotalDeals), 0);
    }, 0);

    // Equity & Floating: ALWAYS current snapshot from latestAllClients (not filter-dependent)
    const equity = cuLatest.reduce((s, l) => s + safeNum(l.Equity), 0);
    const floating = cuLatest.reduce((s, l) => s + safeNum(l.FloatingPL), 0);

    return { deposit, withdraw, volume, profit, equity, floating, trades, totalDeposit, totalWithdraw, totalVolume, totalProfit, totalTrades };
  }, [isAll, selectedLogin, cuLoginKey, dataVersion]);

  const chartData = useMemo(() => {
    const src = isAll ? cuLogins : [cl];
    const allM = [...new Set(src.flatMap(l => (C.historyMap[l.Login] || []).map(h => h.MonthKey)))].sort();
    return allM.map(m => {
      const entry = { month: fmtMonth(m) };
      src.forEach(l => {
        const lid = String(l.Login);
        const row = (C.historyMap[lid] || []).find(h => h.MonthKey === m);
        entry["vol_" + lid] = row ? safeNum(row.Volume) : 0;
        entry["pl_" + lid] = row ? safeNum(row.Profit) : 0;
      });
      return entry;
    });
  }, [isAll, selectedLogin, cuLoginKey, dataVersion]);

  const tradingBehavior = useMemo(() => {
    const src = isAll ? cuLogins : [cl];
    if (!src.length) return [];
    const sn = (f) => isAll ? aggSum(src, f) : safeNum(cl[f]);
    const wT = (f) => isAll ? aggWeightedAvg(src, f, "TotalTrades") : safeNum(cl[f]);
    const wV = (f) => isAll ? aggWeightedAvg(src, f, "Volume") : safeNum(cl[f]);
    const wW = (f) => isAll ? aggWeightedAvg(src, f, "ProfitTrades") : safeNum(cl[f]);
    const wL = (f) => isAll ? aggWeightedAvg(src, f, "LossTrades") : safeNum(cl[f]);
    const totalTrades = sn("TotalTrades");
    const totalWins = sn("ProfitTrades");
    const totalLosses = sn("LossTrades");
    const winPct = totalTrades > 0 ? (totalWins / totalTrades * 100) : 0;
    const lossPct = totalTrades > 0 ? (totalLosses / totalTrades * 100) : 0;
    return [
      ["Most Instrument", isAll ? aggUnion(src, "MostInstrument").join(", ") || "—" : (cl.MostInstrument || "—"), null, "fMostInstrument"],
      ["Instrument Category", isAll ? aggUnion(src, "MostTradedInstruments").join(", ") || "—" : (cl.MostTradedInstruments || "—"), null, "fInstrumentCategory"],
      ["Trade Size", isAll ? fmtNum(wT("MostUsedTradeSize"), 2) : (cl.MostUsedTradeSize || "—"), null, "fTradeSize"],
      ["Most Active Day", isAll ? aggUnion(src, "MostActiveDay").join(", ") || "—" : (cl.MostActiveDay || "—"), null, "fMostActiveDay"],
      ["Daily Volume", fmtNum(sn("DailyVolumeLots"), 2) + " lots", null, "fDailyVolume"],
      ["Trades / Day", fmtNum(sn("TradingFreqPerDay"), 1), null, "fTradesPerDay"],
      ["Total Deals", String(Math.round(sn("TotalDeals"))), null, "fTotalDeals"],
      ["Total Trades", String(Math.round(totalTrades)), null, "fTotalTrades"],
      ["Open Positions", String(Math.round(sn("StillOpenPositions"))), null, "fOpenPositions"],
      ["Open Volume", fmtNum(sn("StillOpenVolumeLots"), 2) + " lots", null, "fOpenVolume"],
      ["Held > 10 Days", String(Math.round(sn("OrdersHeldGT10Days"))), null, "fHeldGT10"],
      ["Vol Held > 10 Days", fmtNum(sn("VolumeHeldGT10DaysLots"), 2) + " lots", null, "fVolHeldGT10"],
      ["Avg Trade Duration", fmtDuration(wT("AvgTradeDuration")), null, "fAvgDuration"],
      ["Avg Close Loss", fmtDuration(wL("AvgTimeCloseLosingTrades")), null, "fAvgCloseLoss"],
      ["Avg Close Profit", fmtDuration(wW("AvgTimeCloseProfitableTrades")), null, "fAvgCloseProfit"],
      ["Net Profit", fmtMoney(sn("TotalNetProfit")), null, "fNetProfit"],
      ["Gross Profit", fmtMoney(sn("GrossProfit")), sn("GrossProfit") > 0 ? T.green : null, "fGrossProfit"],
      ["Gross Loss", fmtMoney(sn("GrossLoss")), sn("GrossLoss") < 0 ? T.red : null, "fGrossLoss"],
      ["Best Trade", fmtMoney(isAll ? aggMax(src, "BestTrade") : safeNum(cl.BestTrade)), T.green, "fBestTrade"],
      ["Worst Trade", fmtMoney(isAll ? aggMin(src, "WorstTrade") : safeNum(cl.WorstTrade)), (isAll ? aggMin(src, "WorstTrade") : safeNum(cl.WorstTrade)) < 0 ? T.red : null, "fWorstTrade"],
      ["Profit Factor", fmtNum(wV("ProfitFactor"), 2), null, "fProfitFactor"],
      ["Long %", fmtNum(wT("LongPct"), 1) + "%", (wT("LongPct") > 90 || wT("LongPct") < 10) ? T.orange : null, "fLongPct"],
      ["Avg Profit Trade", fmtMoney(wW("AvgProfitTrade")), T.green, "fAvgProfitTrade"],
      ["Avg Loss Trade", fmtMoney(wL("AvgLossTrade")), wL("AvgLossTrade") < 0 ? T.red : null, "fAvgLossTrade"],
      ["Max Consec. Wins", String(Math.round(isAll ? aggMax(src, "MaxConsecutiveWins") : safeNum(cl.MaxConsecutiveWins))), (isAll ? aggMax(src, "MaxConsecutiveWins") : safeNum(cl.MaxConsecutiveWins)) >= 15 ? T.red : (isAll ? aggMax(src, "MaxConsecutiveWins") : safeNum(cl.MaxConsecutiveWins)) >= 10 ? T.orange : null, "fMaxConsecWins"],
      ["Expected Payoff", fmtMoney(wT("ExpectedPayoff")), null, "fExpectedPayoff"],
      ["Win Trades", isAll ? Math.round(totalWins) + " (" + fmtNum(winPct, 1) + "%)" : (cl.ProfitTrades || 0) + " (" + fmtNum(safeNum(cl.ProfitPct), 1) + "%)", T.green, "fWinTrades"],
      ["Loss Trades", isAll ? Math.round(totalLosses) + " (" + fmtNum(lossPct, 1) + "%)" : (cl.LossTrades || 0) + " (" + fmtNum(safeNum(cl.LossPct), 1) + "%)", T.red, "fLossTrades"],
      ["Swap In/Out", fmtMoney(sn("SwapInOut")), null, "fSwapInOut"],
      ["Actual Swap", fmtMoney(sn("ActualSwap")), null, "fActualSwap"],
    ];
  }, [isAll, selectedLogin, cuLoginKey, dataVersion]);

  const abuseItems = useMemo(() => {
    const src = isAll ? cuLogins : [cl];
    const sn = (f) => isAll ? aggSum(src, f) : safeNum(cl[f]);
    const wAvg = (f) => isAll ? aggWeightedAvg(src, f, "TotalTrades") : safeNum(cl[f]);
    const wAvgH = (f) => isAll ? aggWeightedAvg(src, f, "HedgeDealsCount") : safeNum(cl[f]);
    const riskItems = [];
    if (isAll) {
      const bRisk = aggWorstRisk(src, "BehavioralRisk");
      const eRisk = aggWorstRisk(src, "EquityRisk");
      const maxS = src.reduce((m, l) => (l.healthScore || 0) > (m.healthScore || 0) ? l : m, src[0]);
      const minScore = Math.min(...src.map(l => l.healthScore || 0));
      const allFlagsStr = cuAllFlags.length > 0 ? cuAllFlags.join(", ") : "Normal";
      const groupOrder = getGroupConfig().groupOrder || ["STD_B","STD_AB","PRO_A","SPD_A","HDG_A","B2B_A","CRT_A"];
      let worstGL = src[0];
      src.forEach(l => { if (groupOrder.indexOf(l._clientGroup) > groupOrder.indexOf(worstGL._clientGroup)) worstGL = l; });
      const otherG = [...new Set(src.map(l => l._clientGroup))].filter(g => g !== worstGL._clientGroup);
      const anyArb = src.some(l => l._groupResult && l._groupResult.isArbitrage);
      const arbCnt = src.filter(l => l._groupResult && l._groupResult.isArbitrage).length;
      const gcfg = getGroupConfig();
      const gDef = gcfg.groups[worstGL._clientGroup];
      const gCol = gDef ? gDef.color : T.textMuted;
      riskItems.push(
        ["Behavioral Risk", React.createElement("span", { style: { display: "inline-flex", alignItems: "center", gap: 6 } }, React.createElement("span", { style: { padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600, color: RISK_C[bRisk.value], background: (RISK_C[bRisk.value] || T.textMuted) + "18" } }, bRisk.value), React.createElement("span", { style: { fontSize: 10, color: T.textMuted } }, "(" + bRisk.dist + ")")), null, "fBehavioralRisk"],
        ["Equity Risk", React.createElement("span", { style: { display: "inline-flex", alignItems: "center", gap: 6 } }, React.createElement("span", { style: { padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600, color: RISK_C[eRisk.value], background: (RISK_C[eRisk.value] || T.textMuted) + "18" } }, eRisk.value), React.createElement("span", { style: { fontSize: 10, color: T.textMuted } }, "(" + eRisk.dist + ")")), null, "fEquityRisk"],
        ["Broker Risk Score", String(maxS.healthScore) + " — " + healthLabel(maxS.healthScore) + " (range: " + minScore + "–" + maxS.healthScore + ")", healthColor(maxS.healthScore), "fBrokerRiskScore"],
        ["Client Profiling", allFlagsStr, T.amber, "fClientProfiling"],
        ["Client Group", React.createElement("span", { style: { display: "inline-flex", alignItems: "center", gap: 6 } }, React.createElement("span", { style: { padding: "3px 10px", borderRadius: 4, fontSize: 11, fontWeight: 700, color: gCol, background: gCol + "18", border: "1px solid " + gCol + "30" } }, worstGL._clientGroup), otherG.length > 0 ? React.createElement("span", { style: { fontSize: 10, color: T.textMuted } }, "(" + otherG.join(", ") + ")") : null), null, "fClientGroup"],
        ["Arbitrage Suspected", anyArb ? "YES (" + arbCnt + " of " + src.length + ")" : "No", anyArb ? T.red : T.green, "fArbSuspected"],
      );
    } else {
      const score = cl.healthScore || calcHealthScore(cl);
      riskItems.push(
        ["Behavioral Risk", cl.BehavioralRisk || "—", cl.BehavioralRisk === "High" ? T.red : T.green, "fBehavioralRisk"],
        ["Equity Risk", cl.EquityRisk || "—", cl.EquityRisk === "High" ? T.red : (cl.EquityRisk === "Moderate" ? T.orange : T.green), "fEquityRisk"],
        ["Broker Risk Score", score + " — " + healthLabel(score), healthColor(score), "fBrokerRiskScore"],
        ["Client Profiling", cl.ClientProfiling || "Normal", cl.ClientProfiling !== "Normal" ? T.orange : T.green, "fClientProfiling"],
        ["Client Group", React.createElement(GroupBadge, { group: cl._clientGroup, size: "lg", client: cl }), null, "fClientGroup"],
        ["Arbitrage Suspected", cl._groupResult && cl._groupResult.isArbitrage ? "YES" : "No", cl._groupResult && cl._groupResult.isArbitrage ? T.red : T.green, "fArbSuspected"],
      );
    }
    const metrics = [
      ["Speed <30s", fmtNum(wAvg("PctUnder30Sec"), 1) + "%", wAvg("PctUnder30Sec") > 30 ? T.red : wAvg("PctUnder30Sec") > 10 ? T.orange : T.green, "fSpeed30"],
      ["Speed <2min", fmtNum(wAvg("PctUnder2Min"), 1) + "%", wAvg("PctUnder2Min") > 50 ? T.red : wAvg("PctUnder2Min") > 25 ? T.orange : T.green, "fSpeed2m"],
      ["Speed <5min", fmtNum(wAvg("PctUnder5Min"), 1) + "%", wAvg("PctUnder5Min") > 70 ? T.red : wAvg("PctUnder5Min") > 40 ? T.orange : T.green, "fSpeed5m"],
      ["Speed Consistency", fmtNum(wAvg("SpeedConsistency"), 2), wAvg("SpeedConsistency") < 0.3 ? T.red : wAvg("SpeedConsistency") < 0.5 ? T.orange : T.green, "fSpeedConsistency"],
      ["Fast Win Rate", fmtNum(wAvg("FastTradeWinRate"), 1) + "%", wAvg("FastTradeWinRate") > 80 ? T.red : wAvg("FastTradeWinRate") > 60 ? T.orange : null, "fFastWinRate"],
      ["Slow Win Rate", fmtNum(wAvg("SlowTradeWinRate"), 1) + "%", null, "fSlowWinRate"],
      ["Hedge Ratio", fmtNum(wAvg("HedgeRatio"), 2), wAvg("HedgeRatio") > 0.5 ? T.orange : T.green, "fHedgeRatio"],
      ["Hedge Deals", String(Math.round(sn("HedgeDealsCount"))), null, "fHedgeDeals"],
      ["Hedge Volume", fmtNum(sn("HedgeVolumeLots"), 2) + " lots", null, "fHedgeVolume"],
      ["Avg Hedge Duration", fmtDuration(wAvgH("AvgHedgeDurationSec")), null, "fAvgHedgeDuration"],
      ["Friday Late Opens", String(Math.round(sn("FridayLateOpens"))), sn("FridayLateOpens") > 5 ? T.red : sn("FridayLateOpens") > 0 ? T.orange : T.green, "fFridayLateOpens"],
      ["Friday Late Open Lots", fmtNum(sn("FridayLateOpenLots"), 2) + " lots", sn("FridayLateOpenLots") > 1 ? T.red : null, "fFridayLateLots"],
      ["Friday Hedge Breaks", String(Math.round(sn("FridayHedgeBreaks"))), sn("FridayHedgeBreaks") > 3 ? T.red : T.green, "fFridayHedgeBreaks"],
      ["Friday HB Lots", fmtNum(sn("FridayHedgeBreakLots"), 2) + " lots", sn("FridayHedgeBreakLots") > 1 ? T.red : null, "fFridayHBLots"],
      ["News Trades (10min)", String(Math.round(sn("TradesNearNews10Min"))), sn("TradesNearNews10Min") > 20 ? T.orange : T.green, "fNewsTrades"],
      ["Commission Exploit", fmtMoney(sn("CommissionRebateExploitation")), sn("CommissionRebateExploitation") > 0 ? T.red : T.green, "fCommissionExploit"],
      ["Remaining Swap", fmtMoney(sn("RemainingSwap")), null, "fRemainingSwap"],
      ["Login Same IP", isAll ? aggUnion(src, "Login_Same_IP").join(", ") || "None" : (cl.Login_Same_IP || "None"), (isAll ? aggUnion(src, "Login_Same_IP").length : cl.Login_Same_IP) ? T.red : T.green, "fLoginSameIP"],
      ["CU Same IP", isAll ? aggUnion(src, "CU_Same_IP").join(", ") || "None" : (cl.CU_Same_IP || "None"), (isAll ? aggUnion(src, "CU_Same_IP").length : cl.CU_Same_IP) ? T.red : T.green, "fCUSameIP"],
      ["IP ODT", String(Math.round(sn("IP_ODT"))), sn("IP_ODT") > 0 ? T.red : T.green, "fIPODT"],
    ];
    return [...riskItems, ...metrics];
  }, [isAll, selectedLogin, cuLoginKey, dataVersion, cuAllFlags]);

  const accountItems = useMemo(() => {
    const src = isAll ? cuLogins : [cl];
    // Always-current snapshot from latestAllClients (not filter-dependent)
    const latAll = C.latestAllClients || [];
    const cuLatest = isAll
      ? latAll.filter(l => l.CU_Account === cuAccount && l._company === company)
      : latAll.filter(l => String(l.Login) === selectedLogin && l._company === company);
    const curEq = cuLatest.reduce((s, l) => s + safeNum(l.Equity), 0);
    const curEqPD = cuLatest.reduce((s, l) => s + safeNum(l.EquityPrevDay), 0);
    const curEqPM = cuLatest.reduce((s, l) => s + safeNum(l.EquityPrevMonth), 0);
    const curFL = cuLatest.reduce((s, l) => s + safeNum(l.FloatingPL), 0);
    if (isAll) {
      const allClientTypes = aggUnion(src, "ClientType");
      const allIPs = aggUnion(src, "IP");
      const totalDepCount = aggSum(src, "Deposit_Count");
      let earliestReg = null, latestAccess = null, earliestCRM = null;
      src.forEach(l => {
        const reg = new Date(l.Registration); if (!isNaN(reg) && (!earliestReg || reg < earliestReg)) earliestReg = reg;
        const la = new Date(l.LastAccess); if (!isNaN(la) && (!latestAccess || la > latestAccess)) latestAccess = la;
        const crm = new Date(l.CRM_Account_Open_Date); if (!isNaN(crm) && (!earliestCRM || crm < earliestCRM)) earliestCRM = crm;
      });
      const maxLifetime = aggMax(src, "LifetimeDays");
      const relProfit = aggSum(src, "RelatedAccountsTotalProfit");
      let firstDepLogin = src[0];
      src.forEach(l => { if (l.First_Deposit_Time && new Date(l.First_Deposit_Time) < new Date(firstDepLogin.First_Deposit_Time || "9999")) firstDepLogin = l; });
      return [
        ["CU Account", cuAccount, T.cyan, "fCUAccount"],
        ["Client Type", allClientTypes.join(", ") || "—", null, "fClientType"],
        ["Account Manager", primary["A.M"] || "—", null, "fAccountManager"],
        ...(showPhone ? [["Phone", primary.Phone_Number && primary.Phone_Number !== "NULL" ? primary.Phone_Number : "—"]] : []),
        ["Country", primary.Country || "—", null, "fCountry"],
        ["Registration", fmtDate(earliestReg), T.textDim, "fRegistration"],
        ["Last Access", fmtDate(latestAccess), T.green, "fLastAccess"],
        ["CRM Open Date", fmtDate(earliestCRM), T.textDim, "fCRMOpenDate"],
        ["First Deposit", firstDepLogin.First_Deposit ? fmtMoney(firstDepLogin.First_Deposit) : "—", null, "fFirstDeposit"],
        ["First Deposit Time", fmtDate(firstDepLogin.First_Deposit_Time), null, "fFirstDepositTime"],
        ["Deposit Count", String(Math.round(totalDepCount)), null, "fDepositCount"],
        ["Lifetime", maxLifetime > 0 ? Math.round(maxLifetime) + " days" : "—", T.amber, "fLifetime"],
        ["_divider"],
        ["Equity", fmtMoney(curEq), T.green, "fEquity"],
        ["Equity Prev Day", fmtMoney(curEqPD), null, "fEquityPrevDay"],
        ["Equity Prev Month", fmtMoney(curEqPM), null, "fEquityPrevMonth"],
        ["Floating P/L", fmtMoney(curFL), curFL >= 0 ? T.green : T.red, "fFloatingPL"],
        ["Related Profit", fmtMoney(relProfit), relProfit >= 0 ? T.green : T.red, "fRelatedProfit"],
        ["_divider"],
        ["IP Addresses", allIPs.length > 0 ? allIPs.join(", ") : "—", T.textDim, "fIPAddress"],
        ["_note", "Account Type, Status, Leverage, Client Group are per-login — select a specific login to view"],
      ];
    } else {
      const curLogin = cuLatest[0];
      const eqVal = curLogin ? safeNum(curLogin.Equity) : safeNum(cl.Equity);
      const eqPD = curLogin ? safeNum(curLogin.EquityPrevDay) : safeNum(cl.EquityPrevDay);
      const eqPM = curLogin ? safeNum(curLogin.EquityPrevMonth) : safeNum(cl.EquityPrevMonth);
      const flVal = curLogin ? safeNum(curLogin.FloatingPL) : safeNum(cl.FloatingPL);
      return [
        ["CU Account", cuAccount, T.cyan, "fCUAccount"],
        ["Client Type", cl.ClientType || "—", null, "fClientType"],
        ["Account Type", cl.Account_Type || "—", null, "fAccountType"],
        ["Client Group", React.createElement(GroupBadge, { group: cl._clientGroup, size: "lg", client: cl }), null, "fClientGroup"],
        ["Account Manager", cl["A.M"] || "—", null, "fAccountManager"],
        ...(showPhone ? [["Phone", cl.Phone_Number && cl.Phone_Number !== "NULL" ? cl.Phone_Number : "—"]] : []),
        ["Country", cl.Country || "—", null, "fCountry"],
        ["Registration", fmtDate(cl.Registration), T.textDim, "fRegistration"],
        ["Last Access", fmtDate(cl.LastAccess), T.green, "fLastAccess"],
        ["CRM Open Date", fmtDate(cl.CRM_Account_Open_Date), T.textDim, "fCRMOpenDate"],
        ["First Deposit", cl.First_Deposit ? fmtMoney(cl.First_Deposit) : "—", null, "fFirstDeposit"],
        ["First Deposit Time", fmtDate(cl.First_Deposit_Time), null, "fFirstDepositTime"],
        ["Deposit Count", String(cl.Deposit_Count || "—"), null, "fDepositCount"],
        ["Lifetime", safeNum(cl.LifetimeDays) > 0 ? Math.round(safeNum(cl.LifetimeDays)) + " days" : "—", T.amber, "fLifetime"],
        ["Status", cl.ClientStatus || "—", null, "fStatus"],
        ["Leverage", cl.Leverage || "—", null, "fLeverage"],
        ["_divider"],
        ["IP Address", cl.IP || "—", T.textDim, "fIPAddress"],
        ["Equity", fmtMoney(eqVal), null, "fEquity"],
        ["Equity Prev Day", fmtMoney(eqPD), null, "fEquityPrevDay"],
        ["Equity Prev Month", fmtMoney(eqPM), null, "fEquityPrevMonth"],
        ["Floating P/L", fmtMoney(flVal), flVal >= 0 ? T.green : T.red, "fFloatingPL"],
        ["Related Accounts", (() => { const ra = cl.RelatedAccounts; if (!ra || ra === "NULL" || ra === 0) return "None"; const s = String(ra); if (s.length > 20) return s.replace(/,/g, "").replace(/(\d{5,7})/g, "$1, ").trim().replace(/,\s*$/, ""); return s; })(), null, "fRelatedAccounts"],
        ["Related Profit", fmtMoney(cl.RelatedAccountsTotalProfit), null, "fRelatedProfit"],
      ];
    }
  }, [isAll, selectedLogin, cuLoginKey, dataVersion, showPhone]);

  const handleToggleMonitor = () => { toggleMonitor(baseClient.Login, baseClient._company); setMonitored(!monitored); };

  return (
    <div className="slide-in">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
        <button onClick={() => {
          if (clientHistory.length > 0) { const prev = clientHistory[clientHistory.length - 1]; setClientHistory(clientHistory.slice(0, -1)); setSelectedClient(prev); }
          else if (onBack) onBack();
          else setSelectedClient(null);
        }} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, border: "1px solid " + T.border, background: "transparent", color: T.textDim, cursor: "pointer", fontSize: 13 }}>
          <ChevronLeft size={16} /> Back
        </button>
        <button onClick={handleToggleMonitor} style={{
          display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8,
          border: "1px solid " + (monitored ? T.amber : T.border), background: monitored ? T.amber + "15" : "transparent",
          color: monitored ? T.amber : T.textDim, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
          {monitored ? <EyeOff size={15} /> : <Eye size={15} />}{monitored ? " Monitoring" : " Monitor"}
        </button>
      </div>

      <Card accent={T.cyan} style={{ marginBottom: 20, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg, " + T.cyan + ", " + T.purple + "40, transparent)" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 24, fontWeight: 700, display: "flex", alignItems: "center", gap: 10 }}>
              <CoBadge co={company} size={24} />
              {primary.Name || "CU " + cuAccount}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
              <span className="mono" style={{ color: T.cyan, fontSize: 13, fontWeight: 600 }}>{cuAccount}</span>
              <span style={{ color: T.textMuted }}>•</span>
              <span style={{ fontSize: 12, color: T.amber, fontWeight: 600 }}>{cuLogins.length} Account{cuLogins.length !== 1 ? "s" : ""}</span>
              <span style={{ color: T.textMuted }}>•</span>
              <StatusBadge status={primary.ClientStatus} />
              <span style={{ color: T.textMuted }}>•</span>
              <span style={{ fontSize: 12, color: T.blue, fontWeight: 600 }}>{safeNum(primary.LifetimeDays) > 0 ? Math.round(safeNum(primary.LifetimeDays)) + " days" : "—"}</span>
            </div>
          </div>
          {!isAll && selLogin && (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ textAlign: "center" }}><div style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", marginBottom: 4, fontWeight: 700, letterSpacing: 0.5 }}>B.Risk</div><RiskBadge level={selLogin.BehavioralRisk} /></div>
              <div style={{ textAlign: "center" }}><div style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", marginBottom: 4, fontWeight: 700, letterSpacing: 0.5 }}>E.Risk</div><RiskBadge level={selLogin.EquityRisk} /></div>
              <div style={{ textAlign: "center" }}><div style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", marginBottom: 4, fontWeight: 700, letterSpacing: 0.5 }}>Score</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 40, height: 5, background: T.bgHover, borderRadius: 3, overflow: "hidden" }}><div style={{ height: "100%", width: (selLogin.healthScore || 0) + "%", background: healthColor(selLogin.healthScore), borderRadius: 3 }} /></div>
                  <span className="mono" style={{ fontSize: 12, fontWeight: 700, color: healthColor(selLogin.healthScore) }}>{selLogin.healthScore}</span>
                </div>
              </div>
              <div style={{ textAlign: "center" }}><div style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", marginBottom: 4, fontWeight: 700, letterSpacing: 0.5 }}>Group</div><GroupBadge group={selLogin._clientGroup} size="lg" client={selLogin} /></div>
            </div>
          )}
        </div>
        <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>Profile</span>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {activeFlags.length > 0 ? activeFlags.map(t => (
              <span key={t} style={{ padding: "6px 16px", borderRadius: 8, fontSize: 13, fontWeight: 700, color: TRADER_COLORS[t], background: (TRADER_COLORS[t] || T.textMuted) + "18", border: "1px solid " + (TRADER_COLORS[t] || T.textMuted) + "40", letterSpacing: 0.3 }}>
                {t.replace(/([A-Z])/g, ' $1').trim()}
              </span>
            )) : <span style={{ fontSize: 12, color: T.textMuted }}>No flags</span>}
          </div>
          {isAll && activeFlags.length > 0 && <span style={{ fontSize: 10, color: T.textMuted }}>(combined from all logins)</span>}
        </div>
      </Card>

      <div style={{ background: T.bgCard, border: "1px solid " + T.border, borderRadius: 12, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Account</span>
          <LoginDropdown logins={cuLogins} selected={selectedLogin} onSelect={setSelectedLogin} loginColors={loginColors} />
        </div>
        <div style={{ width: 1, height: 28, background: T.border }} />
        <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1 }}>
          <Calendar size={15} color={T.cyan} style={{ flexShrink: 0 }} />
          <button onClick={() => { setFilterYear("all"); setFilterMonths([]); }}
            style={{ padding: "5px 12px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 11, fontWeight: 700, background: filterYear === "all" ? T.cyan : T.bg, color: filterYear === "all" ? T.bgCard : T.textMuted }}>All</button>
          {availYears.map(y => (
            <button key={y} onClick={() => { setFilterYear(y); setFilterMonths([]); }}
              style={{ padding: "5px 12px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 11, fontWeight: 700, background: filterYear === y ? T.cyan : T.bg, color: filterYear === y ? T.bgCard : T.textMuted }}>{y}</button>
          ))}
          {filterYear !== "all" && <>
            <div style={{ width: 1, height: 20, background: T.border, margin: "0 4px" }} />
            {MNAMES.map((name, i) => {
              const num = i + 1, hasData = availMonthNums.includes(num), isSel = filterMonths.length === 0 || filterMonths.includes(num);
              return (
                <button key={num} onClick={() => hasData && toggleMonth(num)} disabled={!hasData}
                  style={{ padding: "4px 8px", borderRadius: 5, border: "none", cursor: hasData ? "pointer" : "default", fontSize: 10, fontWeight: 600,
                    background: !hasData ? "transparent" : (filterMonths.length === 0 ? T.cyan + "15" : (isSel ? T.cyan : T.bg)),
                    color: !hasData ? T.textMuted + "40" : (filterMonths.length === 0 ? T.cyan : (isSel ? T.bgCard : T.textMuted)),
                    opacity: hasData ? 1 : 0.3 }}>{name}</button>
              );
            })}
          </>}
        </div>
      </div>

      {!isAll && selLogin && (() => {
        const col = loginColors[selectedLogin] || T.cyan;
        return (
          <div style={{ background: col + "08", border: "1px solid " + col + "25", borderRadius: 10, padding: "10px 20px", marginBottom: 16, display: "flex", alignItems: "center", gap: 16, fontSize: 12 }}>
            <span style={{ color: col, fontWeight: 700 }}>Login #{selLogin.Login}</span>
            <span style={{ color: T.textMuted }}>|</span>
            <span style={{ color: T.textDim }}>{selLogin.Account_Type || "—"}</span>
            <span style={{ color: T.textMuted }}>|</span>
            <StatusBadge status={selLogin.ClientStatus} />
          </div>
        );
      })()}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 10, marginBottom: 20 }}>
        {[
          { label: "Deposit", value: financials.deposit, total: financials.totalDeposit, color: T.green, icon: <DollarSign size={18} />, isMoney: true, hasTotal: true },
          { label: "Withdraw", value: financials.withdraw, total: financials.totalWithdraw, color: T.red, icon: <TrendingDown size={18} />, isMoney: true, hasTotal: true },
          { label: "Volume", value: financials.volume, total: financials.totalVolume, color: T.amber, icon: <Zap size={18} />, isMoney: false, hasTotal: true },
          { label: "Profit", value: financials.profit, total: financials.totalProfit, color: financials.profit >= 0 ? T.green : T.red, icon: <Target size={18} />, isMoney: true, hasTotal: true },
          { label: "Equity", value: financials.equity, color: T.cyan, icon: <DollarSign size={18} />, isMoney: true, hasTotal: false },
          { label: "Floating", value: financials.floating, color: financials.floating >= 0 ? T.green : T.red, icon: <Zap size={18} />, isMoney: true, hasTotal: false },
          { label: "Trades", value: financials.trades, total: financials.totalTrades, color: T.blue, icon: <Target size={18} />, isCount: true, hasTotal: true },
        ].map(card => {
          const fmtVal = card.isCount ? fmtNum(card.value, 0) : (card.isMoney ? fmtMoney(card.value) : fmtNum(card.value, 1));
          const fmtTot = card.isCount ? fmtNum(card.total || 0, 0) : (card.isMoney ? fmtMoney(card.total) : fmtNum(card.total, 1));
          return (
            <div key={card.label} style={{ background: T.bgCard, borderRadius: 12, border: "1px solid " + T.border, padding: "14px 12px 12px", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg, " + card.color + ", transparent)" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontSize: 11, color: card.color, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700 }}>{card.label}</div>
                <span style={{ color: card.color, opacity: 0.7 }}>{card.icon}</span>
              </div>
              <div className="mono" style={{ fontSize: 20, fontWeight: 800, color: card.value < 0 ? T.red : T.text, marginBottom: 4 }}>{fmtVal}</div>
              {card.hasTotal && <div style={{ fontSize: 12, color: T.textDim }}>Total: <span style={{ color: (card.total || 0) < 0 ? T.red : T.text, fontWeight: 700 }}>{fmtTot}</span></div>}
            </div>
          );
        })}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        <Card accent={T.amber}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Volume Timeline</div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
              <XAxis dataKey="month" tick={{ fontSize: 9, fill: T.textDim }} angle={-45} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 9, fill: T.textDim }} />
              <Tooltip content={<CustomTooltip />} />
              {(isAll ? cuLogins : [cl]).map(l => {
                const lid = String(l.Login); const col = loginColors[lid] || T.cyan;
                return <Area key={lid} type="monotone" dataKey={"vol_" + lid} name={"#" + lid} stroke={col} fill={col + "20"} strokeWidth={2} dot={false} />;
              })}
              {(isAll && cuLogins.length > 1) && <Legend wrapperStyle={{ fontSize: 10, color: T.textDim }} />}
            </AreaChart>
          </ResponsiveContainer>
        </Card>
        <Card accent={T.green}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Monthly Profit / Loss</div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
              <XAxis dataKey="month" tick={{ fontSize: 9, fill: T.textDim }} angle={-45} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 9, fill: T.textDim }} />
              <Tooltip content={<CustomTooltip />} />
              {(isAll ? cuLogins : [cl]).map(l => {
                const lid = String(l.Login); const col = loginColors[lid] || T.cyan;
                return <Area key={lid} type="monotone" dataKey={"pl_" + lid} name={"#" + lid} stroke={col} fill={col + "15"} strokeWidth={2} dot={false} />;
              })}
              {(isAll && cuLogins.length > 1) && <Legend wrapperStyle={{ fontSize: 10, color: T.textDim }} />}
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
        <DetailSection title="Trading Behavior" subtitle={"Showing " + fmtMonth(cl.MonthKey || C.latestMonth) + " (latest month)"} items={tradingBehavior} />

        <Card accent={T.red}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, color: T.red }}>⚠ Abuse Detection</div>
          <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 12 }}>Showing {fmtMonth(cl.MonthKey || C.latestMonth)} (latest month)</div>
          {abuseItems.map(([k, v, c, helpId], i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid " + T.border + "15", fontSize: 12 }}>
              <span style={{ color: T.textDim, display: "flex", alignItems: "center", gap: 4 }}>{k}{helpId && <HelpTip id={helpId} size={13} />}</span>
              {React.isValidElement(v) ? v : <span className="mono" style={{ fontWeight: 500, color: c || T.text, maxWidth: 260, textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v != null ? v : "—"}</span>}
            </div>
          ))}
          <RiskTimeline logins={cuLogins} selectedLogin={selectedLogin} historyMap={C.historyMap} loginColors={loginColors} />
          <GroupTimelineCmp logins={cuLogins} selectedLogin={selectedLogin} loginColors={loginColors} groupHistoryMap={C.groupHistoryMap} />
        </Card>

        <Card accent={T.blue}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Account Info</div>
          {accountItems.map((item, i) => {
            if (item[0] === "_divider") return <div key={i} style={{ height: 1, background: T.border, margin: "10px 0" }} />;
            if (item[0] === "_note") return (
              <div key={i} style={{ marginTop: 10, padding: "8px 12px", borderRadius: 6, background: T.cyan + "08", border: "1px solid " + T.cyan + "15", fontSize: 11, color: T.textDim }}>{item[1]}</div>
            );
            const [k, v, c, helpId] = item;
            return (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid " + T.border + "15", fontSize: 12 }}>
                <span style={{ color: T.textDim, display: "flex", alignItems: "center", gap: 4 }}>{k}{helpId && <HelpTip id={helpId} size={13} />}</span>
                {React.isValidElement(v) ? v : <span className="mono" style={{ fontWeight: 500, color: c || T.text, maxWidth: 260, textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v != null ? v : "—"}</span>}
              </div>
            );
          })}
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid " + T.border }}>
            <div style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10, fontWeight: 700 }}>
              Trading Accounts ({cuLogins.length})
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "28px 60px 1fr 60px 50px 50px", gap: 6, padding: "4px 8px", marginBottom: 4, fontSize: 9, color: T.textMuted, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>
              <span></span><span>Login</span><span>Type</span><span>Status</span><span>Score</span><span>Group</span>
            </div>
            <div style={{ maxHeight: 250, overflowY: "auto" }}>
              {cuLogins.map(l => {
                const lid = String(l.Login);
                const col = loginColors[lid] || T.cyan;
                const gcfg = getGroupConfig();
                const gDef = gcfg.groups[l._clientGroup];
                const gCol = gDef ? gDef.color : T.textMuted;
                const isSel = selectedLogin === lid;
                return (
                  <div key={lid} onClick={() => setSelectedLogin(lid)}
                    style={{ display: "grid", gridTemplateColumns: "28px 60px 1fr 60px 50px 50px", gap: 6, alignItems: "center",
                      padding: "7px 8px", marginBottom: 3, borderRadius: 6, cursor: "pointer", fontSize: 11,
                      background: isSel ? col + "12" : T.bg, border: isSel ? "1px solid " + col + "30" : "1px solid transparent", transition: "all 0.15s" }}
                    onMouseEnter={e => !isSel && (e.currentTarget.style.background = T.bgHover)} onMouseLeave={e => !isSel && (e.currentTarget.style.background = isSel ? col + "12" : T.bg)}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: col }} />
                    <span className="mono" style={{ color: col, fontWeight: 700 }}>#{lid}</span>
                    <span style={{ color: T.textDim, fontSize: 9, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.Account_Type || "—"}</span>
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ width: 5, height: 5, borderRadius: "50%", background: STATUS_COLORS[l.ClientStatus] || T.textMuted }} />
                      <span style={{ fontSize: 9, color: STATUS_COLORS[l.ClientStatus] || T.textMuted }}>{l.ClientStatus}</span>
                    </span>
                    <span className="mono" style={{ fontSize: 10, fontWeight: 700, color: healthColor(l.healthScore) }}>{l.healthScore}</span>
                    <span style={{ padding: "2px 6px", borderRadius: 3, fontSize: 9, fontWeight: 700, color: gCol, background: gCol + "18", textAlign: "center" }}>{l._clientGroup}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════
// ═══ MAIN COMPONENT ═══════════════════════════════════
// ════════════════════════════════════════════════════════
export default function ClientProfiles({ C, selectedClient, setSelectedClient, filterYear, setFilterYear, filterMonths, setFilterMonths, showPhone, onBack }) {
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ status: "all", behavioral: "all", equity: "all", acctType: "all", traderType: "all", am: "all" });
  const [clientHistory, setClientHistory] = useState([]);

  const ams = useMemo(() => [...new Set(C.clients.map(c => c["A.M"]).filter(a => a && a !== "NULL" && a !== "X"))].sort(), [C.clients]);
  const acctTypes = useMemo(() => {
    const types = new Set();
    C.clients.forEach(c => {
      const at = c.Account_Type || "";
      at.split(/[\|\\\/]+/).forEach(part => {
        const clean = part.trim().split(/[\s\-\d]+/)[0].replace(/[^A-Za-z]/g, "").toUpperCase();
        if (clean && clean.length > 1 && !/^\d+$/.test(clean) && !["NULL","X","OC","MU","SO","SOAOJ"].includes(clean)) types.add(clean);
      });
    });
    return [...types].sort();
  }, [C.clients]);

  const availYears = [...new Set((C.allMonths || []).map(m => String(m).slice(0, 4)))].sort();
  const availMonthNums = (C.allMonths || []).filter(m => filterYear === "all" || String(m).startsWith(filterYear)).map(m => parseInt(String(m).slice(4)));
  const toggleMonth = (num) => {
    if (filterMonths.includes(num)) setFilterMonths(filterMonths.filter(m => m !== num));
    else setFilterMonths([...filterMonths, num].sort((a, b) => a - b));
  };

  const hasAnyFilter = search || filters.status !== "all" || filters.behavioral !== "all" || filters.equity !== "all" || filters.acctType !== "all" || filters.traderType !== "all" || filters.am !== "all" || filterYear !== "all" || filterMonths.length > 0;
  const clearAll = () => { setSearch(""); setFilters({ status: "all", behavioral: "all", equity: "all", acctType: "all", traderType: "all", am: "all" }); setFilterYear("all"); setFilterMonths([]); };

  const filtered = useMemo(() => {
    const list = C.healthScores || C.clients;
    return list.filter(c => {
      if (search) { const s = search.toLowerCase(); if (!String(c.Login).includes(s) && !(c.Name || "").toLowerCase().includes(s) && !(c.CU_Account || "").toLowerCase().includes(s)) return false; }
      if (filters.status !== "all" && c.ClientStatus !== filters.status) return false;
      if (filters.behavioral !== "all" && c.BehavioralRisk !== filters.behavioral) return false;
      if (filters.equity !== "all" && c.EquityRisk !== filters.equity) return false;
      if (filters.acctType !== "all" && !(c.Account_Type || "").toUpperCase().includes(filters.acctType)) return false;
      if (filters.traderType !== "all" && c[filters.traderType] !== filters.traderType) return false;
      if (filters.am !== "all" && c["A.M"] !== filters.am) return false;
      return true;
    });
  }, [C.healthScores, C.clients, search, filters]);

  const selStyle = (v, curr) => ({
    padding: "6px 12px", borderRadius: 6,
    border: "1px solid " + (v === curr ? T.cyan : T.border),
    background: v === curr ? T.cyanGlow : T.bgInput,
    color: v === curr ? T.cyan : T.textDim,
    cursor: "pointer", fontSize: 11, fontWeight: 500
  });

  const columns = [
    { key: "CU_Account", label: "CU", render: (v) => <span className="mono" style={{ color: T.amber, fontSize: 11 }}>{v || "—"}</span> },
    { key: "Login", label: "Login", render: (v, row) => <span className="mono" style={{ display: "flex", alignItems: "center", gap: 6, color: T.cyan }}><CoBadge co={row._company} size={16} />{v}</span> },
    { key: "Name", label: "Name", render: v => v || "—" },
    { key: "ClientStatus", label: "Status", render: v => <StatusBadge status={v} /> },
    { key: "Account_Type", label: "Type", render: v => <Badge text={v || "—"} color={T.purple} bg={T.purpleBg} /> },
    { key: "BehavioralRisk", label: "B.Risk", render: v => <RiskBadge level={v} /> },
    { key: "EquityRisk", label: "E.Risk", render: v => <RiskBadge level={v} /> },
    { key: "healthScore", label: "Risk Score", render: (v, row) => {
      const s = row.healthScore || 0;
      return <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <div style={{ width: 40, height: 5, background: T.bgHover, borderRadius: 3, overflow: "hidden" }}><div style={{ height: "100%", width: s + "%", background: healthColor(s), borderRadius: 3 }} /></div>
        <span style={{ fontSize: 10, color: healthColor(s), fontWeight: 600 }}>{s}</span>
      </div>;
    }},
    { key: "_clientGroup", label: "Group", render: (v, r) => <GroupBadge group={v} client={r} /> },
    { key: "Equity", label: "Equity", render: v => <span className="mono">{fmtMoney(v)}</span> },
    { key: "Volume", label: "Volume", render: v => <span className="mono">{fmtNum(safeNum(v), 1)}</span> },
    { key: "Profit", label: "Profit", render: v => <span className="mono" style={{ color: safeNum(v) >= 0 ? T.green : T.red }}>{fmtMoney(v)}</span> },
    { key: "A.M", label: "A.M", render: v => <span style={{ fontSize: 10, color: T.textDim }}>{(v || "—").substring(0, 15)}</span> },
  ];

  if (selectedClient) {
    return <ClientDetailView C={C} selectedClient={selectedClient} setSelectedClient={setSelectedClient}
      clientHistory={clientHistory} setClientHistory={setClientHistory} showPhone={showPhone} onBack={onBack}
      filterYear={filterYear} setFilterYear={setFilterYear} filterMonths={filterMonths} setFilterMonths={setFilterMonths} />;
  }

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 20 }}>Client Profiles</h1>
      <div style={{ position: "sticky", top: 0, zIndex: 10, paddingBottom: 12, background: T.bg }}>
        <div style={{ background: T.bgCard, border: "1px solid " + T.border, borderRadius: 12, padding: "12px 16px", marginBottom: 10, display: "flex", alignItems: "center", gap: 14 }}>
          <Calendar size={15} color={T.cyan} style={{ flexShrink: 0 }} />
          <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
            <button onClick={() => { setFilterYear("all"); setFilterMonths([]); }}
              style={{ padding: "5px 12px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 11, fontWeight: 700, background: filterYear === "all" ? T.cyan : T.bg, color: filterYear === "all" ? T.bgCard : T.textMuted }}>All</button>
            {availYears.map(y => (
              <button key={y} onClick={() => { setFilterYear(y); setFilterMonths([]); }}
                style={{ padding: "5px 12px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 11, fontWeight: 700, background: filterYear === y ? T.cyan : T.bg, color: filterYear === y ? T.bgCard : T.textMuted }}>{y}</button>
            ))}
          </div>
          <div style={{ width: 1, height: 24, background: T.border, flexShrink: 0 }} />
          <div style={{ display: "flex", gap: 3, flex: 1 }}>
            {MNAMES.map((name, i) => {
              const num = i + 1, hasData = availMonthNums.includes(num), isSel = filterMonths.length === 0 || filterMonths.includes(num);
              return (
                <button key={num} onClick={() => hasData && toggleMonth(num)} disabled={!hasData}
                  style={{ padding: "5px 10px", borderRadius: 5, border: "none", cursor: hasData ? "pointer" : "default", fontSize: 10, fontWeight: 600,
                    background: !hasData ? "transparent" : (filterMonths.length === 0 ? T.cyan + "15" : (isSel ? T.cyan : T.bg)),
                    color: !hasData ? T.textMuted + "40" : (filterMonths.length === 0 ? T.cyan : (isSel ? T.bgCard : T.textMuted)),
                    opacity: hasData ? 1 : 0.3 }}>{name}</button>
              );
            })}
          </div>
          {(filterYear !== "all" || filterMonths.length > 0) && (
            <button onClick={() => { setFilterYear("all"); setFilterMonths([]); }}
              style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid " + T.border, background: "transparent", color: T.textMuted, cursor: "pointer", fontSize: 10, fontWeight: 600, flexShrink: 0, whiteSpace: "nowrap" }}>✕ Clear</button>
          )}
        </div>
        <Card style={{ background: T.bgCard }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
            <div style={{ flex: 1, position: "relative" }}>
              <Search size={16} color={T.textDim} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by Login, Name, or CU..."
                style={{ width: "100%", padding: "10px 12px 10px 38px", borderRadius: 8, border: "1px solid " + T.border, background: T.bgInput, color: T.text, fontSize: 13, outline: "none" }} />
            </div>
            <span className="mono" style={{ fontSize: 12, color: T.textDim, whiteSpace: "nowrap" }}>{filtered.length} clients</span>
            {hasAnyFilter && (
              <button onClick={clearAll}
                style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid " + T.red + "30", background: T.redBg, color: T.red, cursor: "pointer", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>✕ Clear All</button>
            )}
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ display: "flex", gap: 4 }}>
              {["all", "Active", "Not Active", "Dormant"].map(v => (
                <button key={v} onClick={() => setFilters(f => ({ ...f, status: v }))} style={selStyle(v, filters.status)}>{v === "all" ? "All Status" : v}</button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              {["all", "Normal", "High"].map(v => (
                <button key={v} onClick={() => setFilters(f => ({ ...f, behavioral: v }))} style={selStyle(v, filters.behavioral)}>{v === "all" ? "All B.Risk" : v}</button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              {["all", "Low", "Moderate", "High"].map(v => (
                <button key={v} onClick={() => setFilters(f => ({ ...f, equity: v }))} style={selStyle(v, filters.equity)}>{v === "all" ? "All E.Risk" : v}</button>
              ))}
            </div>
            <select value={filters.acctType} onChange={e => setFilters(f => ({ ...f, acctType: e.target.value }))}
              style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid " + T.border, background: T.bgInput, color: T.text, fontSize: 11 }}>
              <option value="all">All Account Types</option>
              {acctTypes.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <select value={filters.traderType} onChange={e => setFilters(f => ({ ...f, traderType: e.target.value }))}
              style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid " + T.border, background: T.bgInput, color: T.text, fontSize: 11 }}>
              <option value="all">All Trader Types</option>
              {TRADER_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <select value={filters.am} onChange={e => setFilters(f => ({ ...f, am: e.target.value }))}
              style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid " + T.border, background: T.bgInput, color: T.text, fontSize: 11, maxWidth: 180 }}>
              <option value="all">All Managers</option>
              {ams.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        </Card>
      </div>
      <DataTable exportTitle="Client-Profiles" columns={columns} data={filtered} onRowClick={setSelectedClient} pageSize={25} />
    </div>
  );
}
